import { Box } from '@mui/material';
import AdminUser from 'AdminUser';
import React from 'react';
import { MarkdownRenderer } from '../../MarkdownRenderer';
import StyledFieldDescription from '../../styles/StyledFieldDescription';
import { StyledInput } from '../../styles/StyledInputs';
import { GuardrailEditorToolbar } from './GuardrailEditorToolbar';
import { adminLinkLabel } from './guardrailEditorUtils';
import { useAdminLinkSearch } from './useAdminLinkSearch';
import { useMarkdownFieldActions } from './useMarkdownFieldActions';

type GuardrailMessageEditorProps = {
  value: string;
  onChange: (value: string) => void;
  /** Shown as greyed placeholder text when the field is empty. */
  placeholder?: string;
};

function GuardrailPreview({ value }: { value: string }) {
  return (
    <>
      <StyledFieldDescription stacked sx={{ mt: 1, mb: 0.5 }}>
        Preview (links open only if the protocol is allowed):
      </StyledFieldDescription>
      <Box
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          p: 1.5,
          bgcolor: 'background.paper',
          minHeight: 48,
        }}
      >
        {value.trim() ? (
          <MarkdownRenderer>{value}</MarkdownRenderer>
        ) : (
          <Box component="span" sx={{ color: 'text.disabled' }}>
            Nothing to preview.
          </Box>
        )}
      </Box>
    </>
  );
}

export default function GuardrailMessageEditor({
  value,
  onChange,
  placeholder,
}: GuardrailMessageEditorProps) {
  const { inputRef, replaceSelection, wrap, insertLink } = useMarkdownFieldActions(value, onChange);
  const { adminOptions, adminLoading, searchAdmins, clearAdminOptions } = useAdminLinkSearch();

  const insertAdminLink = (admin: AdminUser | null) => {
    const email = admin?.email;
    if (!email) return;
    replaceSelection((selected) => {
      const label = selected || adminLinkLabel(admin);
      const text = `[${label}](mailto:${email})`;
      return { text, selectionStart: text.length, selectionEnd: text.length };
    });
    clearAdminOptions();
  };

  return (
    <Box sx={{ mt: 1 }}>
      <GuardrailEditorToolbar
        onBold={() => wrap('**')}
        onItalic={() => wrap('*')}
        onLink={insertLink}
        adminOptions={adminOptions}
        adminLoading={adminLoading}
        onAdminSearch={searchAdmins}
        onAdminSelect={insertAdminLink}
      />

      <StyledInput
        multiline
        minRows={4}
        fullWidth
        value={value}
        placeholder={placeholder}
        onChange={(event) => onChange(event.target.value)}
        ref={inputRef}
      />

      <GuardrailPreview value={value} />
    </Box>
  );
}
